import moment from 'moment';
import { formatDate } from '@angular/common';
import { DateFormat } from './enums';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { REGEX_PATTERNS } from './constants';

/**
 * Formats a given date string to either date or time format based on the flag.
 * Example 2023-08-04T03:30:00 to 27.09.2023
 *
 * @param dateStr The date string to be formatted.
 * @param formatAsDate Flag indicating whether to format as date (true) or time (false).
 * @returns The formatted date or time string, or an empty string if the input is invalid.
 */
export const formatDateOrTime = (dateStr: string, formatAsDate: boolean = true): string => {
  if (!dateStr) {
    return '';
  }
  const date = moment(dateStr).local(); // Convert to local time zone
  const format = formatAsDate ? DateFormat.DATE : DateFormat.TIME;
  return date.format(format);
};

/**
 * Validates if the given string is a valid date format.
 *
 * @param value The date string to be validated.
 * @returns True if the date is valid, false otherwise.
 */
export const isValidDate = (value: string): boolean => REGEX_PATTERNS.DATE.test(value);

/**
 * Validates if the given string is a valid time format.
 *
 * @param value The time string to be validated.
 * @returns True if the time is valid, false otherwise.
 */
export const isValidTime = (value: string): boolean => REGEX_PATTERNS.TIME.test(value);

/**
 * Converts a given date string to the local time zone and formats it.
 *
 * @param dateStr The date string to be converted.
 * @returns The formatted date string in the local time zone or an empty string if the input is invalid.
 */
export const formatDateToLocalTimeZone = (dateStr: any): string => {
  if (dateStr) {
    const utcDate = new Date(dateStr);
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const localDate = new Date(utcDate.toLocaleString('en-US', { timeZone }));
    const formattedDate = formatDate(localDate, DateFormat.DATE_TIME, 'en-US', timeZone);
    return formattedDate;
  }
  return '';
};

/**
 * Capitalizes the first letter of a given string.
 *
 * @param str The string to be capitalized.
 * @returns The capitalized string.
 */
export const capitalizeFirstLetter = (str: string) => {
  if (!str) {
    return str;
  }
  return str.charAt(0).toUpperCase() + str.slice(1);
};

/**
 * Checks if the values of two form fields do not match.
 *
 * @param field1 - The name or identifier of the first field.
 * @param field2 - The name or identifier of the second field.
 * @param form - The form control that contains the fields to be checked.
 * @param translateService - The TranslateService instance for translation.
 *
 * @returns `true` if the values of the two fields do not match, `false` otherwise.
 */
export const checkFieldMismatch = (field1: string, field2: string, form: AbstractControl, translateService: TranslateService): boolean => {
  const value1 = form.get(field1)?.value;
  const value2 = form.get(field2)?.value;
  if (value1 && value2 !== value1) {
    const errorMessage = translateService.instant('Do not match', { label: capitalizeFirstLetter(field1) });
    form.get(field2)?.setErrors({ message: errorMessage });
    return true;
  }
  return false;
};

export const formatDateOutput = (dateStr: string | number | Date) => {
  return dateStr ? formatDate(dateStr, 'yyyy-MM-dd', 'en_US') : null;
};

/**
 * Converts a local time string to UTC.
 * @param {string} time - The local time in 'HH:mm' format.
 * @returns {string} - The converted UTC time in 'HH:mm' format.
 */
export const convertTimeToUTC = (time: string): string => {
  // Parse the local time and convert to UTC
  const localTime = moment(time, 'HH:mm'); // Parse as local time
  return localTime.utc().format('HH:mm'); // Convert to UTC and format
};

export const convertToFormData = (rawValue: Record<string, any>): any => {
  const formData = new FormData();
  Object.entries(rawValue).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      formData.append(key, JSON.stringify(value));
    } else if (value instanceof Blob || value instanceof File) {
      formData.append(key, value);
    } else {
      formData.append(key, value ?? '');
    }
  });
  return formData;
};

export const numberLengthValidator = (controlName: string, length: number): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control?.parent?.get(controlName)?.value;
    if (value) {
      if (value.length !== length) {
        return { invalidLength: true };
      }
    }
    return null;
  };
};

export const signingType = (value: string): string => {
  const GUID_D = /^[0-9a-f]{8}-([0-9a-f]{4}-){3}[0-9a-f]{12}$/i;
  return GUID_D.test(value.trim()) ? "Xml" : "Pdf";
};

const PDF_VIEWER_LANGUAGE_MAP: Record<string, string> = {
  no: 'nb-NO',
  // en-US locale in ngx-extended-pdf-viewer 26.x is missing legacy sidebar keys used by the viewer UI.
  en: 'en-GB',
};

export const getPdfViewerLanguage = (langCode?: string): string => {
  return PDF_VIEWER_LANGUAGE_MAP[langCode ?? ''] ?? 'nb-NO';
};
